import { cleanGluedDuplicateUnits } from "../../../utils/answerUnitUtils";

// Marks can be fractional once a fixed-total group is split across its parts (5/3 ≈ 1.67). Show up to
// two decimals but drop trailing zeros so whole marks stay clean ("2", not "2.00").
export const fmtMarks = (n) => {
  const num = Number(n ?? 0);
  return Number.isInteger(num) ? String(num) : String(Math.round(num * 100) / 100);
};

const MATH_DELIMS = [["$$", "$$"], ["\\[", "\\]"], ["\\(", "\\)"], ["$", "$"]];

/**
 * Cut a preview to `max` characters without ever splitting a math span.
 *
 * Slicing at a fixed count used to land inside "$2 \times 2 \times 3 \times 3 = 36$", so the preview
 * kept the opening "$" and lost its partner. HtmlWithLatex then wrapped the orphaned commands
 * (wrapBareLatex) and the accordion header printed a raw "$2 $\times$ 2 $\times$ 3" above a row that
 * rendered "2 × 2 × 3 × 3 = 36" perfectly — same stem, different cut.
 *
 * A span that straddles the limit is kept whole when it finishes within `slack`, since dropping it
 * leaves a preview ending at "i.e." that says nothing; otherwise the cut falls back to the last
 * point where no span was open.
 */
export const cutOutsideMath = (text, max, slack = 40) => {
  let i = 0;
  let open = null;
  let lastOutside = 0;
  let closeIdx = -1;

  while (i < text.length) {
    // "\$" is a printed dollar sign, never a delimiter.
    if (text[i] === "\\" && text[i + 1] === "$") {
      i += 2;
      if (!open && i <= max) lastOutside = i;
      continue;
    }
    if (open) {
      if (text.startsWith(open[1], i)) {
        i += open[1].length;
        open = null;
        if (i <= max) lastOutside = i;
        else if (closeIdx < 0) closeIdx = i;
        continue;
      }
      i += 1;
      continue;
    }
    const at = i;
    const found = MATH_DELIMS.find((d) => text.startsWith(d[0], at));
    if (found) {
      open = found;
      i += found[0].length;
      continue;
    }
    i += 1;
    if (i <= max) lastOutside = i;
  }

  if (closeIdx > 0 && closeIdx <= max + slack) return text.slice(0, closeIdx).trimEnd();
  // A command name cut mid-word ("\tim") would render as literal text.
  return text.slice(0, Math.min(lastOutside, max)).replace(/\\[A-Za-z]*$/, "").trimEnd();
};

export const truncateHtml = (html, max) => {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  const text = (div.textContent || div.innerText || "").trim();
  if (text) return text.length > max ? cutOutsideMath(text, max) + "…" : text;
  if (/<img\b/i.test(String(html)) || /<svg\b/i.test(String(html))) return "See figure";
  return "";
};

// A drawing answer is an array of [gx,gy] points / lines — don't dump raw coordinates into a
// breakdown cell; the plotted shape is shown on the Review page.
export const formatAnswerLabel = (val) => {
  if (!val) return val;
  try {
    const parsed = JSON.parse(val);
    if (Array.isArray(parsed)) {
      if (parsed.length > 0 && parsed.every((p) => Array.isArray(p))) return "Shape drawn";
      return parsed.flat(Infinity).map((v) => String(v ?? "").trim()).filter(Boolean).join(" / ");
    }
    // A matching answer arrives as {"<option id>": "<match>"}; printing the object showed raw JSON
    // in the "Correct Answer" column. Show the matches in order instead.
    if (parsed && typeof parsed === "object") {
      return Object.values(parsed).map((v) => String(v ?? "").trim()).filter(Boolean).join(" / ");
    }
  } catch { /* not JSON */ }
  if (typeof val === "string") return cleanGluedDuplicateUnits(val);
  return val;
};

/**
 * A plain-text answer typed or stored one item per line ("10p, 10p" / "10p, 5p, 5p" — "List all the
 * ways …", NLIGSC G2 Mock 1 Q42) keeps its lines; HTML collapsed them into one run-on string.
 * Anything already HTML is returned untouched.
 */
export const plainLinesToHtml = (val) => {
  if (typeof val !== "string" || !/\n/.test(val.trim()) || /<\/?[a-z][^>]*>/i.test(val)) return val;
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return val.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => `<div>${esc(l)}</div>`).join("");
};

/**
 * Review "Correct answer" label (Grok Bot QA 2026-10-15, round 3). Unanswered table / image-fill /
 * multi-fill rows received the key as a JSON list or object (`[["-5"],["2"]]`, `{"0":"x"}`) and the
 * review printed it raw. Show the cell values in order instead; coordinate lists stay "Shape drawn".
 */
export const formatReviewCorrectLabel = (val) => {
  if (typeof val !== "string") return val;
  const t = val.trim();
  if (!(t.startsWith("[") || t.startsWith("{"))) return val;
  let parsed;
  try { parsed = JSON.parse(t); } catch { return val; }
  if (!parsed || typeof parsed !== "object") return val;
  const isPoint = (p) => Array.isArray(p) && p.length === 2 && p.every((n) => typeof n === "number");
  if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(isPoint)) return "Shape drawn";
  const flat = [];
  const walk = (v) => {
    if (v == null) return;
    if (Array.isArray(v)) { v.forEach(walk); return; }
    if (typeof v === "object") { Object.values(v).forEach(walk); return; }
    const s = String(v).replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
    if (s !== "") flat.push(s);
  };
  walk(parsed);
  return flat.length ? flat.join(", ") : "—";
};
